// HtmlCanvas.jsx
import React, { useEffect, useRef } from "react";

const HtmlCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Basic rectangle
    ctx.fillStyle = "#007bff";
    ctx.fillRect(20, 20, 150, 100);

    // Stroke rectangle
    ctx.strokeStyle = "#ff0000";
    ctx.lineWidth = 4;
    ctx.strokeRect(200, 20, 150, 100);

    // Circle
    ctx.beginPath();
    ctx.arc(100, 200, 50, 0, 2 * Math.PI);
    ctx.fillStyle = "#28a745";
    ctx.fill();
    ctx.stroke();

    // Text
    ctx.font = "20px Arial";
    ctx.fillStyle = "#000";
    ctx.fillText("HTML Canvas", 200, 180);

    // Line
    ctx.beginPath();
    ctx.moveTo(200, 220);
    ctx.lineTo(350, 350);
    ctx.strokeStyle = "#ffa500";
    ctx.lineWidth = 3;
    ctx.stroke();
  }, []);

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <h1>HTML Canvas - Graphics in Web</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          The <strong>HTML &lt;canvas&gt;</strong> element is used to draw graphics on a web page via JavaScript. It supports shapes, images, text, and animations.
        </p>
      </section>

      {/* Canvas Element */}
      <section id="canvas-element">
        <h2>2. Canvas Element</h2>
        <p>
          The basic HTML canvas syntax:
        </p>
        <pre>
          <code>{`<canvas id="myCanvas" width="400" height="300"></canvas>`}</code>
        </pre>
        <canvas
          ref={canvasRef}
          id="myCanvas"
          width="400"
          height="400"
          style={{ border: "1px solid #ccc", marginTop: "10px" }}
        />
      </section>

      {/* Canvas Context */}
      <section id="canvas-context">
        <h2>3. Canvas Context</h2>
        <p>
          The <strong>2D rendering context</strong> is used to draw shapes, text, and images.
        </p>
        <pre>
          <code>{`const canvas = document.getElementById('myCanvas');
const ctx = canvas.getContext('2d');`}</code>
        </pre>
      </section>

      {/* Drawing Shapes */}
      <section id="drawing-shapes">
        <h2>4. Drawing Shapes</h2>
        <ul>
          <li><strong>Rectangles:</strong> fillRect(), strokeRect(), clearRect()</li>
          <li><strong>Paths:</strong> beginPath(), moveTo(), lineTo(), arc(), closePath()</li>
          <li><strong>Circles & Arcs:</strong> ctx.arc(x, y, radius, startAngle, endAngle)</li>
        </ul>
      </section>

      {/* Drawing Text */}
      <section id="drawing-text">
        <h2>5. Drawing Text</h2>
        <ul>
          <li>ctx.font = "20px Arial";</li>
          <li>ctx.fillText("Hello", x, y);</li>
          <li>ctx.strokeText("Hello", x, y);</li>
        </ul>
      </section>

      {/* Drawing Lines */}
      <section id="drawing-lines">
        <h2>6. Drawing Lines</h2>
        <ul>
          <li>ctx.beginPath();</li>
          <li>ctx.moveTo(startX, startY);</li>
          <li>ctx.lineTo(endX, endY);</li>
          <li>ctx.stroke();</li>
        </ul>
      </section>

      {/* Colors & Styles */}
      <section id="colors-styles">
        <h2>7. Colors & Styles</h2>
        <ul>
          <li>ctx.fillStyle = "#color";</li>
          <li>ctx.strokeStyle = "#color";</li>
          <li>ctx.lineWidth = value;</li>
          <li>ctx.globalAlpha = 0.5;</li>
        </ul>
      </section>

      {/* Images */}
      <section id="images">
        <h2>8. Images on Canvas</h2>
        <p>
          Load and draw images using <strong>drawImage()</strong>:
        </p>
        <pre>
          <code>{`const img = new Image();
img.src = 'example.jpg';
img.onload = () => {
  ctx.drawImage(img, x, y, width, height);
};`}</code>
        </pre>
      </section>

      {/* Animations */}
      <section id="animations">
        <h2>9. Animations</h2>
        <p>
          Use <strong>requestAnimationFrame()</strong> to animate shapes:
        </p>
        <pre>
          <code>{`function animate() {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  // draw shapes
  requestAnimationFrame(animate);
}
animate();`}</code>
        </pre>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>10. Best Practices</h2>
        <ul>
          <li>Always define width and height attributes (not just CSS).</li>
          <li>Use requestAnimationFrame for smooth animations.</li>
          <li>Clear canvas before redrawing shapes for animations.</li>
          <li>Keep canvas accessible by providing fallback content.</li>
        </ul>
      </section>
    </div>
  );
};

export default HtmlCanvas;
